import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Check, Info, Loader2, Wrench } from "lucide-react";
import { formatDecimal } from "@/components/QuickImportDialog";

// Üç adımlı, SALT OKUNUR içeri alma prototipi. Yalnız /quick-import/preview çağırır; confirm, katalog ve stok yazımı bu bileşende YOK.
// Sunucu kodları arayüze çıkmaz: her kod üç sınıftan birine düşer (kullanıcı düzeltir / belge kaynaklı / sistem). Bilinmeyen kod = sistem.

const STEPS = ["Faturayı kontrol et", "Kalemleri düzenle", "Sonucu gözden geçir"];

const FIX = {
  LINE_CLASS_UNRESOLVED: [1, "Ürün mü hizmet mi olduğunu seçin."],
  LINE_CLASS_REQUIRED: [1, "Ürün mü hizmet mi olduğunu seçin."],
  PRODUCT_KIND_REQUIRED: [1, "Ürün mü hizmet mi olduğunu seçin."],
  PRODUCT_NOT_MATCHED: [1, "Bu satır için ürünü seçin ya da hizmet olarak işaretleyin."],
  STOCK_PRODUCT_UNRESOLVED: [1, "Stoğa girmesi için listeden ürünü seçin."],
  PRODUCT_MATCH_AMBIGUOUS: [1, "Birden fazla ürün uyuyor; doğru ürünü seçin."],
  PRODUCT_DRAFT_CANNOT_BE_STOCKED: [1, "Hatırlanacak yeni ürün stoğa alınamaz; mevcut ürünü seçin ya da stok girişini kapatın."],
  CATALOG_DUPLICATE: [1, "Bu ürün katalogda zaten var; listeden mevcut ürünü seçin."],
  PRODUCT_DRAFT_DUPLICATE: [1, "Bu ürün katalogda zaten var; listeden mevcut ürünü seçin."],
  PRODUCT_CHOICE_INVALID: [1, "Seçtiğiniz ürün artık geçerli değil; yeniden seçin."],
  REFERENCE_INVALID: [1, "Seçtiğiniz kayıt artık geçerli değil; yeniden seçin."],
  SUPPLIER_NOT_MATCHED: [0, "Tedarikçiyi seçin."],
  SUPPLIER_MATCH_AMBIGUOUS: [0, "Birden fazla tedarikçi uyuyor; doğru tedarikçiyi seçin."],
  SUPPLIER_CHOICE_INVALID: [0, "Seçilen tedarikçi bu faturaya uymuyor; yeniden seçin."],
  WAREHOUSE_REQUIRED: [1, "Stoğa girecek ürün satırları için depo seçin."],
  WAREHOUSE_INVALID: [1, "Seçilen depo kullanılamıyor; başka bir depo seçin."],
  STOCK_NOT_APPLICABLE: [1, "Stoğa girecek ürün satırı yok; stok girişini kapatın."],
  SOURCE_CHANGED: [0, "Fatura sağlayıcıda güncellendi; pencereyi kapatıp yeniden açın."],
  PREVIEW_EXPIRED: [0, "Önizlemenin süresi doldu; pencereyi kapatıp yeniden açın."],
};

const DOCUMENT = {
  TOTAL_MISMATCH: "Fatura toplamı satırlardan hesaplanan tutarla uyuşmuyor.",
  PROVIDER_TOTAL_MISSING: "Faturada ödenecek toplam yok.",
  TOTALS_INVALID: "Fatura tutarları hesaplanamadı.",
  LINE_TOTAL_MISMATCH: "Satır tutarı miktar × fiyat − iskonto ile uyuşmuyor.",
  LINE_VAT_MISMATCH: "Satırın KDV tutarı faturadakiyle uyuşmuyor.",
  LINE_FIELD_MISSING: "Satırda miktar, fiyat, KDV veya açıklama eksik.",
  LINE_DISCOUNT_UNREADABLE: "Satırdaki iskonto okunamadı.",
  EXEMPTION_REQUIRED: "KDV istisna kodu gerekiyor; bu ekranda girilemez.",
  EXEMPTION_CODE_REQUIRED: "KDV istisna kodu gerekiyor; bu ekranda girilemez.",
  DOCUMENT_NUMBER_REQUIRED: "Faturada numara yok.",
  DOCUMENT_NUMBER_MISSING: "Faturada numara yok.",
  CURRENCY_NOT_SUPPORTED: "Faturanın para birimi bu akışta desteklenmiyor.",
  DOCUMENT_INVALID: "Fatura bu akışla okunamadı.",
  SUPPLIER_DATA_INVALID: "Faturadaki tedarikçi bilgileri eksik veya geçersiz.",
};

// Önceki/süren içeri alma: belge hatası DEĞİL; tedarikçiye yönlendirilmez. Her biri ayrı başlık + ne yapılacağı.
const IMPORT_STATE = {
  // Sunucu kodu kaydın HANGİ defterde olduğunu (çekirdek/eski stok işlemi) ayırmaz: liste adı söylenmez.
  DUPLICATE_DOCUMENT: ["Bu fatura için daha önce oluşturulmuş bir kayıt var.", "Aynı fatura ikinci kez içeri alınamaz."],
  // Kod etkin, durumu bilinmeyen ve geçersiz taslağı birlikte kapsar: "sürüyor" denmez.
  OPEN_IMPORT_EXISTS: ["Bu fatura için daha önce açılmış bir içeri alma kaydı var.", "Bu kaydın durumu netleşmeden fatura yeniden içeri alınamaz."],
  PRIOR_IMPORT_FAILED: ["Bu fatura için önceki içeri alma denemesi tamamlanamadı.", "Deneme inceleniyor; inceleme bitene kadar fatura yeniden içeri alınamaz."],
};

const NOTICE = {
  LINE_VAT_DIFFERS_FROM_UBL: "Satır KDV'si faturadaki tutardan farklı hesaplandı.",
  LINE_NET_DIFFERS_FROM_UBL: "Satır tutarı faturadaki tutardan farklı hesaplandı.",
};

const SYSTEM_HINT = {
  CANDIDATE_INDEX_NOT_READY: "Ürün ve tedarikçi eşleştirmesi şu an kullanılamıyor.",
  CANDIDATES_INCOMPLETE: "Ürün ve tedarikçi eşleştirmesi şu an kullanılamıyor.",
  WAREHOUSE_INDEX_NOT_READY: "Depo listesi şu an kullanılamıyor.",
};
const SYSTEM_DEFAULT = "İçeri alma altyapısı bu fatura için henüz hazır değil.";

// {kind: fix|document|notice|system, text, step}.  Metin hiçbir zaman sunucu kodunu içermez.
export function classifyIssue(key) {
  if (FIX[key]) return { kind: "fix", step: FIX[key][0], text: FIX[key][1] };
  if (IMPORT_STATE[key]) return { kind: "state", text: IMPORT_STATE[key][0], hint: IMPORT_STATE[key][1] };
  if (DOCUMENT[key]) return { kind: "document", text: DOCUMENT[key] };
  if (NOTICE[key]) return { kind: "notice", text: NOTICE[key] };
  return { kind: "system", text: SYSTEM_HINT[key] || SYSTEM_DEFAULT };
}

const EMPTY_CHOICE = { kind: null, product_id: null, new_product_draft: false, stock: null, vat_exemption_code: null };
const UNIT_FALLBACK = "Birim yok";

function money(value, currency) {
  const text = formatDecimal(value);
  if (text == null) return "—";
  return `${text} ${currency === "TRY" ? "₺" : currency || ""}`.trim();
}

function quantity(value, unit) {
  const text = formatDecimal(value, { minFraction: 0, trimZeros: true });
  if (text == null) return "—";
  return `${text} ${unit?.label || unit?.code || UNIT_FALLBACK}`;
}

function payload(mode, lineChoices, createSupplier, supplierId, warehouseId) {
  return { mode, warehouse_id: mode === "expense_and_stock" ? warehouseId : null, supplier_id: supplierId,
    create_supplier: supplierId ? false : createSupplier, line_choices: lineChoices };
}

// Tüm kaynaklardaki kodlar tek listede: satır numarası korunur, aynı satırda aynı metin tekrarlanmaz.
function collectIssues(decision, view) {
  const raw = [...(decision?.blocker_items || []), ...(decision?.review_items || [])].map((i) => ({ key: i?.key, line: Number.isInteger(i?.line_index) ? i.line_index : null }));
  for (const key of view?.document?.document_issues || []) raw.push({ key, line: null });
  for (const key of view?.supplier?.issues || []) raw.push({ key, line: null });
  for (const l of view?.lines || []) for (const key of l.issues || []) raw.push({ key, line: l.index });
  const supplierActionable = (view?.supplier?.candidates || []).length > 0 || view?.decisions?.supplier?.create_new?.available === true;
  const seen = new Set();
  const out = [];
  for (const { key, line } of raw) {
    let c = classifyIssue(key);
    // Seçecek aday yoksa "tedarikçiyi seçin" demek yanıltıcıdır: kullanıcıya düzeltme yükü bindirmeden sistem engeline düşer.
    if (c.kind === "fix" && c.step === 0 && /^SUPPLIER_/.test(String(key)) && !supplierActionable) c = { kind: "system", text: "Tedarikçi bu hesapta eşleştirilemedi." };
    const id = `${c.kind}|${line}|${c.text}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ ...c, line });
  }
  return out;
}

function lineLabel(line) {
  return line == null ? "" : `Satır ${line + 1}: `;
}

function Segmented({ value, options, onChange, name }) {
  return (
    <div role="radiogroup" aria-label={name} className="inline-flex rounded-lg border border-border p-0.5">
      {options.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}
          className={`rounded-md px-3 py-1 text-sm ${value === v ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>{text}</button>
      ))}
    </div>
  );
}

function StepHeader({ step, onStep, counts }) {
  return (
    <ol className="grid grid-cols-3 gap-2" data-testid="wizard-steps">
      {STEPS.map((title, i) => (
        <li key={title}>
          <button type="button" onClick={() => onStep(i)} aria-current={step === i ? "step" : undefined}
            className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${step === i ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${counts[i] ? "bg-amber-100 text-amber-900" : "bg-muted"}`}>{i + 1}</span>
            <span className="min-w-0 truncate">{title}</span>
            {counts[i] ? <span className="ml-auto text-xs text-amber-900" data-testid={`step-count-${i}`}>{counts[i]} eksik</span> : null}
          </button>
        </li>
      ))}
    </ol>
  );
}

function SystemNotice({ items }) {
  if (!items.length) return null;
  const texts = [...new Set(items.map((i) => i.text))];
  return (
    <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="system-blocker" role="status">
      <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">Sistem tarafında bekleyen hazırlık var</p>
        {texts.map((t) => <p key={t}>{t}</p>)}
        <p className="mt-1 text-xs text-slate-600">Bu sizin düzeltebileceğiniz bir eksik değil. Fatura bu hazırlık tamamlanınca içeri alınabilir.</p>
      </div>
    </div>
  );
}

function DocumentProblems({ items }) {
  if (!items.length) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="document-problems" role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">Bu fatura bu akışla içeri alınamaz</p>
        {items.map((i) => <p key={`${i.line}-${i.text}`}>{lineLabel(i.line)}{i.text}</p>)}
        <p className="mt-1 text-xs">Faturanın kendisinden kaynaklanıyor; tedarikçiden düzeltilmiş fatura isteyin veya gelişmiş işlemi kullanın.</p>
      </div>
    </div>
  );
}

function ImportState({ items }) {
  if (!items.length) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950" data-testid="import-state" role="status">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{items.map((i) => <div key={i.text}><p className="font-medium">{i.text}</p><p className="text-xs">{i.hint}</p></div>)}</div>
    </div>
  );
}

function FixList({ items, onGo, testid = "fix-list" }) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" data-testid={testid}>
      <p className="font-medium">Tamamlamanız gerekenler</p>
      <ul className="mt-1 space-y-1">
        {items.map((i) => (
          <li key={`${i.line}-${i.text}`} className="flex items-center justify-between gap-2">
            <span>{lineLabel(i.line)}{i.text}</span>
            {onGo ? <Button size="sm" variant="outline" className="h-7" onClick={() => onGo(i.step)}>{STEPS[i.step]}</Button> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckStep({ view, decision, issues, createSupplier, onSupplier, onCreateSupplier }) {
  const doc = view.document || {};
  const supplier = view.supplier || {};
  const recon = decision?.document_summary?.reconciliation;
  const candidates = supplier.candidates || [];
  return (
    <div className="space-y-3" data-testid="step-check">
      <div className="grid gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
        <div><p className="text-xs text-muted-foreground">Fatura no</p><p className="font-semibold">{doc.number || "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Tarih</p><p className="font-semibold">{doc.issue_date ? new Date(`${doc.issue_date}T00:00:00`).toLocaleDateString("tr-TR") : "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Fatura tutarı</p><p className="font-semibold" data-testid="legal-total">{money(doc.legal_payable, doc.currency)}</p></div>
      </div>
      {recon?.status === "mismatch" || doc.reconciliation === "mismatch" ? (
        <p className="text-sm text-red-900">Satırlardan hesaplanan toplam {money(recon?.computed_payable ?? doc.computed_payable, doc.currency)}; fatura toplamıyla {money(String(recon?.difference ?? doc.difference ?? "").replace(/^-/, ""), doc.currency)} fark var.</p>
      ) : null}

      <div className="rounded-xl border border-border p-4" data-testid="supplier-field">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Tedarikçi</p>
            <p className="truncate font-semibold">{supplier.name || "—"}</p>
            {supplier.tax_number ? <p className="text-xs text-muted-foreground">VKN/TCKN {supplier.tax_number}</p> : null}
          </div>
          {supplier.state === "matched" ? <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Kayıtlı tedarikçiyle eşleşti</span> : null}
        </div>
        {supplier.state !== "matched" && candidates.length ? <div className="mt-3 space-y-1">
          <p className="text-sm">Bu vergi numarasıyla kayıtlı tedarikçilerden birini seçin:</p>
          <div className="flex flex-wrap gap-2" data-testid="supplier-candidates">
            {candidates.map((c) => (
              <Button key={c.id} size="sm" variant={supplier.selected_id === c.id ? "default" : "outline"} aria-pressed={supplier.selected_id === c.id}
                onClick={() => onSupplier(supplier.selected_id === c.id ? null : c.id)}>{c.label || "Adsız tedarikçi"}</Button>
            ))}
          </div>
        </div> : null}
        {supplier.state !== "matched" && !candidates.length && view.decisions?.supplier?.create_new?.available ? <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant={createSupplier ? "default" : "outline"} aria-pressed={createSupplier} onClick={() => onCreateSupplier(!createSupplier)}>Yeni tedarikçi olarak ekle</Button>
          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-900">Önizleme</span>
          <span className="text-xs text-muted-foreground">Bugün yalnız önizlenir; tedarikçi kaydı yapılmaz.</span>
        </div> : null}
      </div>

      <ImportState items={issues.filter((i) => i.kind === "state")} />
      <DocumentProblems items={issues.filter((i) => i.kind === "document")} />
      <SystemNotice items={issues.filter((i) => i.kind === "system")} />
      <FixList items={issues.filter((i) => i.kind === "fix" && i.step === 0)} />
    </div>
  );
}

function newIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid || `wh-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const CREATE_ERRORS = {
  WAREHOUSE_NAME_TAKEN: "Bu adla bir depo zaten var. Listeyi yenileyip o depoyu seçebilirsiniz.",
  IDEMPOTENCY_KEY_REUSED: "Bu istek daha önce farklı bir adla gönderilmiş. Formu kapatıp yeniden açın.",
};

// Yerel depo oluşturma: yalnız yönetim açık + liste BAŞARIYLA boş geldiğinde. Oluşturma depoyu SEÇMEZ; "Listeyi yenile" aynı kararlarla önizler.
function WarehouseCreate({ onRefresh }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [key, setKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const start = () => { setOpen(true); setName(""); setError(null); setCreated(null); setKey(newIdempotencyKey()); };
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post("/warehouses", { name }, { headers: { "Idempotency-Key": key } });
      setCreated(result?.name || name.trim());
      setOpen(false);
      setKey(null);                                        // başarıdan sonra yeni istek yeni anahtar alır
    } catch (err) {
      setError({ text: CREATE_ERRORS[err?.code] || "Depo şu an oluşturulamıyor. Biraz sonra yeniden deneyin.", refresh: err?.code === "WAREHOUSE_NAME_TAKEN" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="warehouse-create">
      {!open ? <Button size="sm" variant="outline" onClick={start}>Depo tanımla</Button> : (
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="warehouse-create-name" aria-label="Depo adı" className="h-8 rounded-md border border-border px-2 text-sm" maxLength={80}
            value={name} onChange={(e) => setName(e.target.value)} placeholder="Depo adı" />
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>Depoyu oluştur</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Vazgeç</Button>
        </div>
      )}
      {error ? <p className="text-xs text-amber-900" data-testid="warehouse-create-error">{error.text}</p> : null}
      {created ? <p className="text-xs text-emerald-800" data-testid="warehouse-created">{created} oluşturuldu. Seçmek için listeyi yenileyin.</p> : null}
      {created || error?.refresh ? <Button size="sm" variant="outline" onClick={onRefresh}>Listeyi yenile</Button> : null}
    </div>
  );
}

// Depo kararı belge düzeyinde TEK yerde: seçim tüm stok satırlarına uygulanır. Otomatik seçim yalnız ekranda açıkça bildirilir.
function WarehousePicker({ warehouse, onWarehouse, canCreate, onRefresh, autoSelectedId }) {
  const options = warehouse.options || [];
  const autoName = autoSelectedId && warehouse.selected_id === autoSelectedId
    && warehouse.selection_available === true && warehouse.options_complete === true
    && options.length === 1 && !options[0].outside_list && options[0].id === autoSelectedId
    ? options[0].name : null;
  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm" data-testid="warehouse-decision">
      <div>
        <p className="font-medium">Hangi depoya girsin?</p>
        <p className="text-xs text-muted-foreground">Seçim bu faturadaki tüm stok satırlarına uygulanır.</p>
      </div>
      {autoName ? <p className="flex items-center gap-1 text-sm text-emerald-800" data-testid="warehouse-auto"><Check className="h-4 w-4" /> {autoName} otomatik seçildi; bu hesaptaki tek depo. Değiştirebilirsiniz.</p> : null}
      {warehouse.selection_available ? <div className="flex flex-wrap gap-2" data-testid="warehouse-options">
        {options.map((o) => (
          <Button key={o.id} size="sm" variant={warehouse.selected_id === o.id ? "default" : "outline"} aria-pressed={warehouse.selected_id === o.id}
            onClick={() => onWarehouse(warehouse.selected_id === o.id ? null : o.id)}>{o.name}</Button>
        ))}
        {!options.length ? <span className="text-muted-foreground">Bu hesapta seçilebilecek depo yok; stok girişini kapatabilirsiniz.</span> : null}
      </div> : <p className="text-muted-foreground">Depo listesi şu an alınamıyor; stok girişini kapatabilirsiniz.</p>}
      {canCreate && warehouse.selection_available === true && !options.length ? <WarehouseCreate onRefresh={onRefresh} /> : null}
      {warehouse.selection_available && warehouse.options_complete === false ? <p className="text-xs text-muted-foreground">Yalnız ilk 50 depo gösteriliyor.</p> : null}
      {warehouse.default_warehouse && warehouse.selected_id !== warehouse.default_warehouse.id ? (
        <p className="text-xs">Varsayılan deponuz {warehouse.default_warehouse.name}; kendiliğinden seçilmez. <button type="button" className="underline" onClick={() => onWarehouse(warehouse.default_warehouse.id)}>Bu depoyu seç</button></p>
      ) : null}
    </div>
  );
}

function Fact({ label, children }) {
  return <div className="min-w-0"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="font-medium">{children}</dd></div>;
}

const PRODUCT_STATE_TEXT = { matched: "Kayıtlı ürünle eşleşti", create: "Yeni ürün önizlemesi", unmatched: "Kayıtlı ürün bulunamadı", ambiguous: "Birden fazla kayıtlı ürün uyuyor" };

// Satır: önce düzenli özet (sunucu tutarları, istemcide yeniden hesap YOK). Karar kontrolleri yalnız çözülmesi gerekiyorsa açık; aksi hâlde "Değiştir".
function LineCard({ line, currency, issues, stockOn, onKind, onProduct, onRemember }) {
  const product = line.product || {};
  const candidates = product.candidates || [];
  const fixes = issues.filter((i) => i.kind === "fix");
  const problems = issues.filter((i) => i.kind === "document");
  const notices = issues.filter((i) => i.kind === "notice");
  const needsDecision = fixes.length > 0 || !line.kind || (line.kind === "product" && candidates.length > 0 && !product.selected_id)
    || !!product.remember_selected;                                                       // açık önizleme seçimi görünür kalır
  const [expanded, setExpanded] = useState(false);
  const showControls = needsDecision || expanded;
  const vat = money(line.vat_amount_provider ?? line.vat_amount_computed, currency);
  const kindText = line.kind === "service" ? "Hizmet · yalnız fatura satırı" : line.kind === "product" ? `Ürün${PRODUCT_STATE_TEXT[product.state] ? ` · ${PRODUCT_STATE_TEXT[product.state]}` : ""}` : null;
  return (
    <div className={`rounded-xl border p-4 ${fixes.length ? "border-amber-300" : problems.length ? "border-red-300" : "border-border"}`} data-testid={`line-${line.index}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 font-medium"><span className="mr-2 text-xs text-muted-foreground">Satır {line.index + 1}</span>{line.description || "Açıklama yok"}</p>
        {stockOn && line.kind === "product" && line.stock?.eligible ? <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Stoğa girer</span> : null}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-5" data-testid={`line-facts-${line.index}`}>
        <Fact label="Miktar">{quantity(line.quantity, line.unit)}</Fact>
        <Fact label="Birim fiyat">{money(line.unit_price, currency)}</Fact>
        <Fact label="İskonto">{line.discount ? money(line.discount, currency) : "—"}</Fact>
        <Fact label="KDV">%{line.vat_rate ?? "—"} · {vat}</Fact>
        <Fact label="Toplam">{money(line.line_extension_provider ?? line.net_computed, currency)}</Fact>
      </dl>
      {!showControls && kindText ? <div className="mt-2 flex items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1 text-emerald-800"><Check className="h-4 w-4" /> {kindText}</span>
        <button type="button" className="text-xs underline" onClick={() => setExpanded(true)}>Değiştir</button>
      </div> : null}
      {showControls ? <>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Segmented name={`Satır ${line.index + 1} türü`} value={line.kind} options={[["product", "Ürün"], ["service", "Hizmet"]]} onChange={(v) => onKind(line.index, v)} />
          {line.kind === "service" ? <span className="text-xs text-muted-foreground">Yalnız fatura satırı olarak kalır; stoğa girmez.</span> : null}
        </div>
        {line.kind === "product" ? <div className="mt-3 space-y-2" data-testid={`product-choice-${line.index}`}>
          {product.state === "matched" && !candidates.length ? <p className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Kayıtlı ürünle eşleşti</p> : null}
          {candidates.length ? <div>
            <p className="text-sm">Kayıtlı ürünlerden seçin:</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {candidates.map((c) => (
                <Button key={c.id} size="sm" variant={product.selected_id === c.id ? "default" : "outline"} aria-pressed={product.selected_id === c.id}
                  onClick={() => onProduct(line.index, product.selected_id === c.id ? null : c.id)}>{c.label || "Adsız ürün"}{c.sku ? ` · ${c.sku}` : ""}</Button>
              ))}
            </div>
          </div> : null}
          {/* Katalogda aday varken yeni ürün önerilmez (sunucu CATALOG_DUPLICATE ile reddeder). */}
          {product.state !== "matched" && !product.selected_id && !candidates.length ? <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!product.remember_selected} onChange={(e) => onRemember(line.index, e.target.checked)} data-testid={`remember-${line.index}`} />
            <span>Yeni ürün olarak önizle
              <span className="block text-xs text-muted-foreground">Bu sürümde ürün kataloğa eklenmez; yalnız sonucun nasıl görüneceğini gösterir.</span></span>
          </label> : null}
        </div> : null}
      </> : null}
      {fixes.map((i) => <p key={i.text} className="mt-2 text-sm text-amber-900" role="note">→ {i.text}</p>)}
      {problems.map((i) => <p key={i.text} className="mt-2 text-sm text-red-900" role="note">{i.text}</p>)}
      {notices.map((i) => <p key={i.text} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Info className="h-3 w-3" /> {i.text}</p>)}
    </div>
  );
}

function LinesStep({ view, issues, stockOn, onStock, warehouseManagement, onWarehouse, onRefresh, autoSelectedId, ...handlers }) {
  const decisions = view.decisions || {};
  const byLine = (index) => issues.filter((i) => i.line === index);
  const docFixes = issues.filter((i) => i.kind === "fix" && i.step === 1 && i.line == null);
  const hasStockLine = (view.lines || []).some((l) => l.kind === "product" && l.stock?.eligible);
  const showWarehouse = stockOn && hasStockLine && decisions.warehouse?.required;
  return (
    <div className="space-y-3" data-testid="step-lines">
      {decisions.stock_action?.available ? <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-4" data-testid="stock-decision">
        <div><p className="font-medium">Ürünler stoğa girsin mi?</p><p className="text-xs text-muted-foreground">Kapalıyken depo sorulmaz ve stok hareketi oluşmaz.</p></div>
        <Segmented name="Stok girişi" value={stockOn ? "on" : "off"} options={[["off", "Stoğa girmesin"], ["on", "Stoğa girsin"]]} onChange={(v) => onStock(v === "on")} />
      </div> : null}
      {showWarehouse ? <WarehousePicker warehouse={decisions.warehouse} onWarehouse={onWarehouse} canCreate={warehouseManagement} onRefresh={onRefresh} autoSelectedId={autoSelectedId} /> : null}
      {(view.lines || []).map((line) => (
        <LineCard key={line.index} line={line} currency={view.document?.currency} issues={byLine(line.index)} stockOn={stockOn} {...handlers} />
      ))}
      {docFixes.length ? <FixList items={docFixes} testid="fix-list-lines" /> : null}
    </div>
  );
}

function SummaryRow({ title, value, detail, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border py-3 last:border-0" data-testid={testid}>
      <div className="min-w-0"><p className="font-medium">{title}</p>{detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}</div>
      <p className="text-right font-semibold">{value}</p>
    </div>
  );
}

function ReviewStep({ view, decision, issues, stockOn, onGo }) {
  const doc = view.document || {};
  const supplier = view.supplier || {};
  const effects = new Set((decision?.effect_summary || []).map((e) => e.key));
  // Hazır olmak SUNUCU kararıdır: FAST_READY + business_ready=true ve gösterilecek engel yok. İstemci listesinin boş olması hazır demek değildir.
  const ready = decision?.status === "FAST_READY" && decision?.business_ready === true && !issues.some((i) => i.kind !== "notice");
  const pending = "Eksikler tamamlanınca";
  const warehouse = view.decisions?.warehouse || {};
  const whName = (warehouse.options || []).find((o) => o.id === warehouse.selected_id)?.name;
  const stockLines = (view.lines || []).filter((l) => l.kind === "product" && l.stock?.eligible);
  const remembered = (view.lines || []).filter((l) => l.kind === "product" && l.product?.remember_selected);
  const external = effects.has("EXPENSE_NOT_RECORDED_EXTERNAL");
  const total = money(doc.legal_payable, doc.currency);
  const invoiceState = external ? "Gider kaydını bağlı muhasebe programınız yönetir." : effects.has("EXPENSE_INVOICE_CREATED") ? "Gider faturası olarak Alış & Gider Faturaları'nda görünür." : "Eksikler tamamlanınca oluşur.";
  const stock = !stockOn || !stockLines.length ? null : !whName
    ? { value: "Depo seçilmedi", detail: "Stoğa girecek ürün satırları için depo seçin." }
    : { value: `${stockLines.length} satır → ${whName}`, detail: stockLines.map((l) => `${l.description || `Satır ${l.index + 1}`}: ${quantity(l.quantity, l.unit)}`).join(" · ") };
  return (
    <div className="space-y-3" data-testid="step-review">
      <div className="rounded-xl border border-border px-4" data-testid="result-summary">
        <SummaryRow testid="sum-invoice" title="Alış faturası" value={total} detail={`${doc.number ? `${doc.number} · ` : ""}${supplier.name || "Tedarikçi"}. ${invoiceState}`} />
        <SummaryRow testid="sum-debt" title="Cari borç" value={effects.has("SUPPLIER_DEBT_RECORDED") ? total : effects.has("SUPPLIER_DEBT_NOT_RECORDED_EXTERNAL") ? "Muhasebe programınızda" : pending}
          detail={`${supplier.name || "Tedarikçi"} carisine borç olarak yazılır.${effects.has("NEW_SUPPLIER_CREATED") ? " Yeni tedarikçi yalnız önizlemedir." : ""}`} />
        {stock ? <SummaryRow testid="sum-stock" title="Stok etkisi" value={stock.value} detail={stock.detail} /> : null}
        <SummaryRow testid="sum-payment" title="Ödeme durumu" value="Ödenecek" detail="Bu işlemde ödeme yapılmaz. Ödemeyi sonra hesap, tarih ve tutar seçerek eklersiniz." />
      </div>
      {remembered.length ? <p className="text-xs text-muted-foreground" data-testid="sum-remember">
        Yeni ürün önizlemesi ({remembered.map((l) => l.description || `Satır ${l.index + 1}`).join(", ")}): bu sürümde kataloğa eklenmez.</p> : null}
      <ImportState items={issues.filter((i) => i.kind === "state")} />
      <DocumentProblems items={issues.filter((i) => i.kind === "document")} />
      <SystemNotice items={issues.filter((i) => i.kind === "system")} />
      <FixList items={issues.filter((i) => i.kind === "fix")} onGo={onGo} testid="fix-list-review" />
      {ready ? <p className="flex items-center gap-2 text-sm text-emerald-800" data-testid="review-ready"><Check className="h-4 w-4" /> Bilgiler tamam.</p>
        : <p className="text-sm text-muted-foreground" data-testid="review-not-ready">Fatura henüz hazır değil; içeri alma için sunucu onayı bekleniyor.</p>}
    </div>
  );
}

export default function QuickImportWizard({ open, onClose, invoice, warehouseManagement = false }) {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("expense_only");
  const [lineChoices, setLineChoices] = useState([]);
  const [createSupplier, setCreateSupplier] = useState(false);
  const [supplierId, setSupplierId] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [autoSelectedId, setAutoSelectedId] = useState(null);
  const warehouseTouched = useRef(false);               // kullanıcı depo kararı verdiyse otomatik seçim bir daha yapılmaz (stok kapatılana dek)
  const requestSequence = useRef(0);
  const documentId = String(invoice?.id || "").trim();

  const load = useCallback(async (nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse) => {
    if (!documentId) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const result = await api.post(`/incoming-invoices/${encodeURIComponent(documentId)}/quick-import/preview`, payload(nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse));
      if (requestSequence.current === sequence) { setDecision(result); setFailed(false); }
    } catch (error) {
      if (requestSequence.current === sequence) {
        setDecision(null);
        setFailed(true);
        toast.error("Fatura önizlemesi hazırlanamadı");
      }
    } finally {
      if (requestSequence.current === sequence) setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (!open || !documentId) {
      requestSequence.current += 1;
      return undefined;
    }
    setStep(0); setMode("expense_only"); setLineChoices([]); setCreateSupplier(false); setSupplierId(null); setWarehouseId(null); setDecision(null); setFailed(false);
    setAutoSelectedId(null); warehouseTouched.current = false;
    load("expense_only", [], false, null, null);
    return () => { requestSequence.current += 1; };
  }, [open, documentId, load]);

  const view = decision?.document_view || null;
  const issues = useMemo(() => collectIssues(decision, view), [decision, view]);
  const counts = useMemo(() => [0, 1, 2].map((s) => (s === 2 ? 0 : issues.filter((i) => i.kind === "fix" && i.step === s).length)), [issues]);

  const reload = (patch) => {
    const next = { mode, lineChoices, createSupplier, supplierId, warehouseId, ...patch };
    load(next.mode, next.lineChoices, next.createSupplier, next.supplierId, next.mode === "expense_and_stock" ? next.warehouseId : null);
  };
  const withChoice = (index, patch) => {
    const next = [...lineChoices];
    while (next.length <= index) next.push({ ...EMPTY_CHOICE });
    next[index] = { ...next[index], ...patch };
    return next;
  };
  const chooseKind = (index, kind) => {
    // Hizmet satırında ürün kimliği, ürün taslağı ve stok çelişkilidir (sunucu reddeder): türle birlikte temizlenir.
    const next = withChoice(index, kind === "service" ? { kind, product_id: null, new_product_draft: false, stock: null } : { kind, stock: mode === "expense_and_stock" ? true : null });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseStock = (on) => {
    const productIndexes = (view?.lines || []).filter((l) => l.kind === "product").map((l) => l.index);
    let next = [...lineChoices];
    for (const index of productIndexes) {
      while (next.length <= index) next.push({ ...EMPTY_CHOICE });
      next[index] = { ...next[index], kind: "product", stock: on ? true : null };
    }
    if (!on) next = next.map((c) => ({ ...c, stock: c.stock === true ? null : c.stock }));
    const nextMode = on ? "expense_and_stock" : "expense_only";
    setMode(nextMode);
    setLineChoices(next);
    if (!on) { setWarehouseId(null); setAutoSelectedId(null); warehouseTouched.current = false; }   // stok kapanınca depo kararı bırakılır
    reload({ mode: nextMode, lineChoices: next, warehouseId: on ? warehouseId : null });
  };
  const chooseProduct = (index, id) => {
    const next = withChoice(index, { kind: "product", product_id: id, new_product_draft: false });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseRemember = (index, on) => {
    const next = withChoice(index, { kind: "product", product_id: null, new_product_draft: on });
    setLineChoices(next);
    reload({ lineChoices: next });
  };
  const chooseSupplier = (id) => { setSupplierId(id); setCreateSupplier(false); reload({ supplierId: id, createSupplier: false }); };
  const chooseCreateSupplier = (on) => { setCreateSupplier(on); setSupplierId(null); reload({ createSupplier: on, supplierId: null }); };
  const chooseWarehouse = (id) => { warehouseTouched.current = true; setAutoSelectedId(null); setWarehouseId(id); reload({ warehouseId: id }); };

  // Tek depo otomatik seçimi: stok açık, sunucu listesi BAŞARILI ve TAM, tek aktif yerel depo (liste dışı değil), henüz depo yok, kullanıcı karar vermemiş.
  // Aynı kararlarla önizleme yenilenir; depo sunucuda yeniden doğrulanır (pasifleşmişse WAREHOUSE_INVALID döner, döngü olmaz: depo artık seçili).
  useEffect(() => {
    const wh = decision?.document_view?.decisions?.warehouse;
    if (mode !== "expense_and_stock" || warehouseId || warehouseTouched.current || !wh) return;
    const options = wh.options || [];
    if (wh.required !== true || wh.selection_available !== true || wh.options_complete !== true || wh.selected_id) return;
    if (options.length !== 1 || options[0].outside_list || !options[0].id) return;
    setWarehouseId(options[0].id);
    setAutoSelectedId(options[0].id);
    reload({ warehouseId: options[0].id });
  }, [decision]); // eslint-disable-line react-hooks/exhaustive-deps

  const stockOn = mode === "expense_and_stock";
  const body = !view ? null : step === 0
    ? <CheckStep view={view} decision={decision} issues={issues} createSupplier={createSupplier} onSupplier={chooseSupplier} onCreateSupplier={chooseCreateSupplier} />
    : step === 1
      ? <LinesStep view={view} issues={issues} stockOn={stockOn} onStock={chooseStock} onKind={chooseKind} onProduct={chooseProduct} onRemember={chooseRemember} onWarehouse={chooseWarehouse}
          warehouseManagement={warehouseManagement === true} onRefresh={() => reload({})} autoSelectedId={autoSelectedId} />
      : <ReviewStep view={view} decision={decision} issues={issues} stockOn={stockOn} onGo={setStep} />;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose?.(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="quick-import-wizard">
        <DialogHeader>
          <DialogTitle>Faturayı içeri al</DialogTitle>
          <DialogDescription>{invoice?.sender_name || invoice?.supplier_name || "Gelen fatura"} · {invoice?.invoice_no || "e-Fatura"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <StepHeader step={step} onStep={setStep} counts={counts} />
          {loading && !view ? <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Fatura hazırlanıyor</div> : null}
          {!loading && (failed || decision?.document_view_error || (decision && !view)) ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" role="alert" data-testid="view-unavailable">
              Fatura satırları şu an güvenle gösterilemiyor. Biraz sonra yeniden deneyin; sorun sürerse gelişmiş işlemi kullanın.
            </div>
          ) : null}
          {view && !decision?.document_view_error ? <div className={loading ? "pointer-events-none opacity-60" : ""} aria-busy={loading}>{body}</div> : null}
          <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
            <div className="flex items-center gap-2">
              {step > 0 ? <Button variant="outline" onClick={() => setStep(step - 1)}>Geri</Button> : null}
              {step < 2 ? <Button onClick={() => setStep(step + 1)} disabled={!view}>Devam</Button> : (
                <>
                  <span className="text-xs text-muted-foreground" data-testid="readonly-note">Önizleme sürümü: kayıt bu aşamada açık değil.</span>
                  <Button disabled aria-disabled="true">İçeri al</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
